import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tabs, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Switch, 
  message, 
  Popconfirm,
  Tag,
  Space,
  Divider,
  Transfer,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined,
  SafetyOutlined,
  SettingOutlined
} from '@ant-design/icons';
import {
  getPermissions,
  getRoles,
  getUserPermissions,
  createRole,
  updateRole,
  deleteRole,
  assignRoleToUser,
  assignPermissionToUser,
  removeRoleFromUser,
  removePermissionFromUser,
  Permission,
  Role,
  UserWithPermissions
} from '../../services/permissionAPI';
import { userManagementAPI } from '../../services/userManagementAPI';
import { companyAPI } from '../../services/companyAPI';

const { TabPane } = Tabs;
const { Option } = Select;

interface Company {
  id: number;
  name: string;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
}

const PermissionManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('permissions');
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [usersWithPermissions, setUsersWithPermissions] = useState<UserWithPermissions[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [userPermissionModalVisible, setUserPermissionModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithPermissions | null>(null);
  
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [permissionsRes, rolesRes, usersRes, companiesRes, userPermissionsRes] = await Promise.all([
        getPermissions().catch(err => {
          console.error('Permissions error:', err);
          return [];
        }),
        getRoles().catch(err => {
          console.error('Roles error:', err);
          return [];
        }),
        userManagementAPI.getUsers().catch(err => {
          console.error('Users error:', err);
          return { data: [] };
        }),
        companyAPI.getCompanies().catch(err => {
          console.error('Companies error:', err);
          return { data: [] };
        }),
        getUserPermissions().catch(err => {
          console.error('User permissions error:', err);
          return { users: [] };
        })
      ]);

      // API-lər birbaşa array qaytarır, results key-i yoxdur
      setPermissions(Array.isArray(permissionsRes) ? permissionsRes : []);
      setRoles(Array.isArray(rolesRes) ? rolesRes : []);
      setUsers(usersRes.data || []);
      setCompanies(companiesRes.data || []);
      setUsersWithPermissions(userPermissionsRes.users || []);
    } catch (error) {
      console.error('Data loading error:', error);
      message.error('Məlumatlar yüklənmədi: ' + (error as any)?.message);
    } finally {
      setLoading(false);
    }
  };

  // İcazələr cədvəli
  const permissionColumns = [
    {
      title: 'Ad',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Kod',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Kateqoriya',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag color="green">{category}</Tag>
    },
    {
      title: 'Açıqlama',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  // Rollar cədvəli
  const roleColumns = [
    {
      title: 'Ad',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Açıqlama',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'İcazə sayı',
      dataIndex: 'permission_count',
      key: 'permission_count',
      render: (count: number) => <Tag color="blue">{count}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktiv' : 'Deaktiv'}
        </Tag>
      )
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      render: (_: any, record: Role) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEditRole(record)}
          >
            Redaktə
          </Button>
          <Popconfirm
            title="Bu rolu silmək istədiyinizdən əminsiniz?"
            onConfirm={() => handleDeleteRole(record.id)}
            okText="Bəli"
            cancelText="Xeyr"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // İstifadəçi icazələri cədvəli
  const userPermissionColumns = [
    {
      title: 'İstifadəçi',
      key: 'user',
      render: (_: any, record: UserWithPermissions) => (
        <div>
          <div>{record.first_name} {record.last_name}</div>
          <div className="text-gray-500 text-sm">{record.email}</div>
        </div>
      )
    },
    {
      title: 'Rollar',
      key: 'roles',
      render: (_: any, record: UserWithPermissions) => (
        <div>
          {record.roles.map(role => (
            <Tag key={role.id} color="blue" className="mb-1">
              {role.role_name} ({role.company_name})
            </Tag>
          ))}
        </div>
      )
    },
    {
      title: 'İcazə sayı',
      key: 'permission_count',
      render: (_: any, record: UserWithPermissions) => (
        <Tag color="green">{record.all_permissions.length}</Tag>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: UserWithPermissions) => (
        <div>
          {record.is_superuser && <Tag color="red">Superuser</Tag>}
          <Tag color={record.is_active ? 'green' : 'red'}>
            {record.is_active ? 'Aktiv' : 'Deaktiv'}
          </Tag>
        </div>
      )
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      render: (_: any, record: UserWithPermissions) => (
        <Button 
          type="link" 
          icon={<SettingOutlined />}
          onClick={() => handleManageUserPermissions(record)}
        >
          İcazələri idarə et
        </Button>
      ),
    },
  ];

  // Rol əlavə/redaktə
  const handleCreateRole = () => {
    setEditingRole(null);
    form.resetFields();
    setRoleModalVisible(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    form.setFieldsValue({
      name: role.name,
      description: role.description,
      is_active: role.is_active,
      permission_ids: role.permissions.map(p => p.id)
    });
    setRoleModalVisible(true);
  };

  const handleSaveRole = async (values: any) => {
    try {
      let result;
      if (editingRole) {
        result = await updateRole(editingRole.id, values);
        message.success('Rol yeniləndi');
      } else {
        result = await createRole(values);
        message.success('Rol yaradıldı');
      }
      
      setRoleModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Role save error:', error);
      message.error('Rol saxlanılmadı: ' + (error as any)?.message);
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    try {
      await deleteRole(roleId);
      message.success('Rol silindi');
      loadData();
    } catch (error) {
      message.error('Rol silinmədi');
    }
  };

  // İstifadəçi icazələrini idarə et
  const handleManageUserPermissions = (user: UserWithPermissions) => {
    setSelectedUser(user);
    setUserPermissionModalVisible(true);
  };

  const handleAssignRole = async (userId: number, roleId: number, companyId?: number) => {
    try {
      await assignRoleToUser(userId, roleId, companyId);
      message.success('Rol təyin edildi');
      loadData();
    } catch (error) {
      message.error('Rol təyin edilmədi');
    }
  };

  const handleRemoveRole = async (userRoleId: number) => {
    try {
      await removeRoleFromUser(userRoleId);
      message.success('Rol alındı');
      loadData();
    } catch (error) {
      message.error('Rol alınmadı');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">İcazə İdarəetməsi</h1>
        <p className="text-gray-600">Sistem icazələrini, rollarını və istifadəçi icazələrini idarə edin</p>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <span>
              <SafetyOutlined />
              İcazələr
            </span>
          } 
          key="permissions"
        >
          <Card>
            <Table
              columns={permissionColumns}
              dataSource={permissions}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <UserOutlined />
              Rollar
            </span>
          } 
          key="roles"
        >
          <Card>
            <div className="mb-4">
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleCreateRole}
              >
                Yeni Rol
              </Button>
            </div>
            <Table
              columns={roleColumns}
              dataSource={roles}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <SettingOutlined />
              İstifadəçi İcazələri
            </span>
          } 
          key="user-permissions"
        >
          <Card>
            <Table
              columns={userPermissionColumns}
              dataSource={usersWithPermissions}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Rol əlavə/redaktə modalı */}
      <Modal
        title={editingRole ? 'Rolu Redaktə Et' : 'Yeni Rol'}
        open={roleModalVisible}
        onCancel={() => setRoleModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveRole}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Rol Adı"
                rules={[{ required: true, message: 'Rol adı tələb olunur' }]}
              >
                <Input placeholder="Məsələn: Admin, Operator" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Status"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Aktiv" unCheckedChildren="Deaktiv" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Açıqlama"
          >
            <Input.TextArea rows={3} placeholder="Rolun açıqlaması" />
          </Form.Item>

          <Form.Item
            name="permission_ids"
            label="İcazələr"
          >
            <Select
              mode="multiple"
              placeholder="İcazələri seçin"
              optionFilterProp="children"
              showSearch
            >
              {permissions.map(permission => (
                <Option key={permission.id} value={permission.id}>
                  {permission.name} ({permission.category})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item className="mb-0">
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRole ? 'Yenilə' : 'Yarat'}
              </Button>
              <Button onClick={() => setRoleModalVisible(false)}>
                Ləğv et
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* İstifadəçi icazələri modalı */}
      <Modal
        title={selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name} - İcazələr` : ''}
        open={userPermissionModalVisible}
        onCancel={() => setUserPermissionModalVisible(false)}
        footer={null}
        width={1000}
      >
        {selectedUser && (
          <div>
            <div className="mb-4">
              <h3>Hazırki Rollar</h3>
              <div className="mb-2">
                {selectedUser.roles.map(role => (
                  <Tag 
                    key={role.id} 
                    color="blue" 
                    closable
                    onClose={() => handleRemoveRole(role.id)}
                    className="mb-1"
                  >
                    {role.role_name} ({role.company_name})
                  </Tag>
                ))}
              </div>
              
              <Space>
                <Select
                  placeholder="Rol seçin"
                  style={{ width: 200 }}
                  onChange={(roleId) => {
                    Modal.confirm({
                      title: 'Rol təyin et',
                      content: (
                        <div>
                          <p>Bu rolu hansı şirkət üçün təyin etmək istəyirsiniz?</p>
                          <Select
                            placeholder="Şirkət seçin (boş buraxsanız bütün şirkətlər üçün)"
                            style={{ width: '100%' }}
                            allowClear
                            onChange={(companyId) => {
                              handleAssignRole(selectedUser.id, roleId, companyId);
                            }}
                          >
                            {companies.map(company => (
                              <Option key={company.id} value={company.id}>
                                {company.name}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      ),
                      onOk: () => handleAssignRole(selectedUser.id, roleId)
                    });
                  }}
                >
                  {roles.filter(role => role.is_active).map(role => (
                    <Option key={role.id} value={role.id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              </Space>
            </div>

            <Divider />

            <div>
              <h3>Bütün İcazələr ({selectedUser.all_permissions.length})</h3>
              <div className="max-h-60 overflow-y-auto">
                {selectedUser.all_permissions.map(permCode => {
                  const perm = permissions.find(p => p.code === permCode);
                  return (
                    <Tag key={permCode} color="green" className="mb-1">
                      {perm ? perm.name : permCode}
                    </Tag>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PermissionManagement;